import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import './AddClientModal.css';
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from '../../config/googleMaps';
import Loader from '../../mainframes/Loader';

const libraries = ['places', 'geometry', 'drawing'];

const mapContainerStyle = { 
  width: '100%', 
  height: '350px', 
  borderRadius: '8px', 
  marginTop: '10px', 
  border: '1px solid #dee2e6' 
};
const defaultCenter = { lat: 20, lng: 0 };

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const EditClientModal = ({ onClose, onSubmit, clientData }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const [formData, setFormData] = useState({
    entityType: clientData?.entityType || '',
    clientName: clientData?.clientName || '',
    contactPersonName: clientData?.contactPersonName || '',
    contactPersonEmail: clientData?.contactPersonEmail || '',
    contactPersonPhone: clientData?.contactPersonPhone || '',
    vat: clientData?.vat || '',
    clientAddress: clientData?.clientAddress || '',
    clientLatitude: toNumberOrNull(clientData?.clientLatitude),
    clientLongitude: toNumberOrNull(clientData?.clientLongitude),
    gstNumber: clientData?.gstNumber || '',
    panNumber: clientData?.panNumber || '',
    industry: clientData?.industry || '',
    creditLimit: clientData?.creditLimit || 0,
    paymentTerms: clientData?.paymentTerms || 'Net 30',
    notes: clientData?.notes || ''
  });

  const [map, setMap] = useState(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    setFormData({
      entityType: clientData?.entityType || '',
      clientName: clientData?.clientName || '',
      contactPersonName: clientData?.contactPersonName || '',
      contactPersonEmail: clientData?.contactPersonEmail || '',
      contactPersonPhone: clientData?.contactPersonPhone || '',
      vat: clientData?.vat || '',
      clientAddress: clientData?.clientAddress || '',
      clientLatitude: toNumberOrNull(clientData?.clientLatitude),
      clientLongitude: toNumberOrNull(clientData?.clientLongitude),
      gstNumber: clientData?.gstNumber || '',
      panNumber: clientData?.panNumber || '',
      industry: clientData?.industry || '',
      creditLimit: clientData?.creditLimit || 0,
      paymentTerms: clientData?.paymentTerms || 'Net 30',
      notes: clientData?.notes || '',
    });
  }, [clientData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address;

        setFormData(prev => ({ 
          ...prev, 
          clientAddress: address, 
          clientLatitude: lat, 
          clientLongitude: lng 
        }));
        
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }
      }
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setFormData(prev => ({ 
      ...prev, 
      clientLatitude: lat, 
      clientLongitude: lng 
    }));
  };

  const onLoadMap = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const handleSubmit = () => {
    onSubmit(clientData._id, formData);
  };

  if (!isLoaded) return <Loader label="Loading Map..." minHeight="100vh" background="rgba(15, 23, 42, 0.45)" />;

  return (
    <div className="acm-modal-overlay">
      <div className="acm-modal-content">
        <div className="acm-modal-header">
          <h3>Edit Client</h3>
          <button className="acm-close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="acm-modal-body">
          {/* Row 1 */}
          <div className="acm-form-grid-3">
            <div className="acm-form-group">
              <label>Entity Type</label>
              <select name="entityType" className="acm-form-control" value={formData.entityType} onChange={handleChange}>
                <option value="">Select Type</option>
                <option value="Individual">Individual</option>
                <option value="Company">Company</option>
                <option value="Partnership">Partnership</option>
                <option value="LLP">LLP</option>
              </select>
            </div>
            <div className="acm-form-group">
              <label>Client Name *</label>
              <input 
                type="text" 
                name="clientName" 
                className="acm-form-control" 
                value={formData.clientName}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>Contact Person Name</label>
              <input 
                type="text" 
                name="contactPersonName" 
                className="acm-form-control" 
                value={formData.contactPersonName}
                onChange={handleChange} 
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="acm-form-grid-3">
            <div className="acm-form-group">
              <label>Contact Email</label>
              <input 
                type="email" 
                name="contactPersonEmail" 
                className="acm-form-control" 
                value={formData.contactPersonEmail}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>Contact Phone *</label>
              <input 
                type="text" 
                name="contactPersonPhone" 
                className="acm-form-control" 
                value={formData.contactPersonPhone}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>VAT</label>
              <input 
                type="text" 
                name="vat" 
                className="acm-form-control" 
                value={formData.vat}
                onChange={handleChange} 
              />
            </div>
          </div>

          {/* Row 3: GST, PAN */}
          <div className="acm-form-grid-3">
            <div className="acm-form-group">
              <label>GST Number</label>
              <input 
                type="text" 
                name="gstNumber" 
                className="acm-form-control" 
                value={formData.gstNumber}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>PAN Number</label>
              <input 
                type="text" 
                name="panNumber" 
                className="acm-form-control" 
                value={formData.panNumber}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>Industry</label>
              <input 
                type="text" 
                name="industry" 
                className="acm-form-control" 
                value={formData.industry}
                onChange={handleChange} 
              />
            </div>
          </div>

          {/* Row 4: Credit & Payment */}
          <div className="acm-form-grid-2">
            <div className="acm-form-group">
              <label>Credit Limit</label>
              <input 
                type="number" 
                name="creditLimit" 
                className="acm-form-control" 
                value={formData.creditLimit}
                onChange={handleChange} 
              />
            </div>
            <div className="acm-form-group">
              <label>Payment Terms</label>
              <select name="paymentTerms" className="acm-form-control" value={formData.paymentTerms} onChange={handleChange}>
                <option value="Immediate">Immediate</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 60">Net 60</option>
                <option value="Net 90">Net 90</option>
              </select>
            </div>
          </div>

          {/* Client Address */}
          <div className="acm-form-grid-1">
            <div className="acm-form-group">
              <label>Client Address</label>
              <Autocomplete
                onLoad={ref => autocompleteRef.current = ref}
                onPlaceChanged={onPlaceChanged}
              >
                <input 
                  type="text" 
                  name="clientAddress" 
                  value={formData.clientAddress}
                  placeholder="Type to search location..." 
                  className="acm-form-control" 
                  onChange={handleChange}
                />
              </Autocomplete>
            </div>
          </div>

          {/* Notes */}
          <div className="acm-form-grid-1">
            <div className="acm-form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                className="acm-form-control"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Additional notes..."
              ></textarea>
            </div>
          </div>
          
          {/* Map */}
          <div className="acm-form-group">
            <label>Pin Location on Map</label>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={
                formData.clientLatitude 
                  ? { lat: formData.clientLatitude, lng: formData.clientLongitude } 
                  : defaultCenter
              }
              zoom={formData.clientLatitude ? 15 : 2}
              onLoad={onLoadMap}
              onClick={handleMapClick}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true
              }}
            >
              {formData.clientLatitude != null && formData.clientLongitude != null && (
                <Marker 
                  position={{ 
                    lat: formData.clientLatitude, 
                    lng: formData.clientLongitude 
                  }} 
                  draggable={true}
                  onDragEnd={handleMapClick} 
                />
              )}
            </GoogleMap>
          </div>

        </div>
        
        <div className="acm-modal-footer">
          <button className="acm-btn acm-btn-submit" onClick={handleSubmit}>
            UPDATE CLIENT
          </button>
          <button className="acm-btn acm-btn-cancel" onClick={onClose}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditClientModal;
